import type { RegisteredComponent } from "@builder.io/sdk-react";

import CategoryFilters from "~/components/category-filters";
import type { CategoryFiltersProps } from "~/hec/types/plp";

const CategoryFiltersBuilder = (
  props: CategoryFiltersProps & { attributes?: Record<string, unknown> },
) => <CategoryFilters {...props} />;

export const CategoryFiltersDefinition = {
  component: CategoryFiltersBuilder,
  name: "categoryFilters",
  friendlyName: "Category Filters",
  noWrap: true,
  inputs: [
    {
      name: "filterType",
      type: "string",
      friendlyName: "Filter Mode",
      enum: ["Filters", "Link and Image", "Facets"],
      defaultValue: "Filters",
      helperText: "Controls which type of filter UI renders.",
    },
    {
      name: "layoutType",
      type: "string",
      friendlyName: "Layout",
      enum: ["scroll", "wrap", "grid"],
      defaultValue: "scroll",
      // Not used for filterType=Filters — that mode always uses responsive grid
      showIf: `options.get('filterType') !== 'Filters'`,
    },
    // ── FILTERS mode ────────────────────────────────────────────────────────
    {
      name: "list",
      type: "list",
      friendlyName: "Filter Pills",
      showIf: `options.get('filterType') === 'Filters'`,
      helperText:
        "URL-filter pills. Each item sets pmin/pmax or search params.",
      subFields: [
        { name: "Filter Label", type: "string", required: true },
        { name: "Minimum Price", type: "string", helperText: "Sets ?pmin=" },
        { name: "Maximum Price", type: "string", helperText: "Sets ?pmax=" },
        {
          name: "Search parameters",
          type: "string",
          helperText: "Extra query params as a URL-encoded string",
        },
      ],
    },
    // ── LINK_AND_IMAGE mode ─────────────────────────────────────────────────
    {
      name: "links",
      type: "list",
      friendlyName: "Image + Link Tiles",
      showIf: `options.get('filterType') === 'Link and Image'`,
      helperText: "Circular image tiles that link to a URL.",
      subFields: [
        { name: "Label", type: "string", required: true },
        { name: "Link", type: "string", helperText: "Destination URL" },
        {
          name: "Image",
          type: "file",
          allowedFileTypes: ["jpeg", "jpg", "png", "svg", "webp"],
        },
      ],
    },
    // ── FACETS mode ─────────────────────────────────────────────────────────
    {
      name: "facets",
      type: "list",
      friendlyName: "Facet Buttons",
      showIf: `options.get('filterType') === 'Facets'`,
      helperText:
        "CIO facet toggle buttons. Must match facet names/values returned by CIO.",
      subFields: [
        {
          name: "name",
          type: "string",
          required: true,
          helperText: "CIO facet name (e.g. 'brand')",
        },
        {
          name: "value",
          type: "string",
          required: true,
          helperText: "Facet option value (e.g. 'nexcare')",
        },
        {
          name: "displayName",
          type: "string",
          helperText:
            "Label override — falls back to CIO display_name if blank",
        },
      ],
    },
  ],
} satisfies RegisteredComponent;
